/* eslint-disable require-atomic-updates, complexity, no-shadow */
import {
    all,
    call,
    delay,
    fork,
    join,
    put,
    race,
    spawn,
    take,
    takeEvery,
    takeLatest
} from "redux-saga/effects";

import {
    getId,
    injectApiWithContextFactory,
    injectBoundActionCreatorsFactory,
    withRefreshRequest,
} from "Utils/sagaUtils";
import noop from "lodash.noop";
import {
    acceptInvite,
    acceptTermsAndConditions,
    getInvites,
    getLoginUrl,
    loadCurrentUser,
    loginUser,
    logoutUser,
    redeemCode,
    refreshToken as refreshUserToken,
    triggerBackToApplication,
    triggerEmailVerification,
    triggerPasswordReset,
    updatePassword,
    updatePasswordInApp
} from "./auth.api";
import {
    LOAD_CURRENT_USER_REQUESTED,
    LOGIN_MANUAL_REFRESH_REQUESTED,
    LOGIN_RELOGIN_REQUESTED,
    LOGIN_REQUESTED,
    LOGIN_SUCCEEDED,
    LOGIN_URL_REQUESTED,
    LOGOUT_REQUESTED,
    REFRESH_SUCCEEDED,
    REQUEST_ACCEPT_TERMS_AND_CONDITIONS,
    TRIGGER_BACK_TO_APPLICATION,
    TRIGGER_EMAIL_VERIFICATION_EMAIL,
    TRIGGER_RESET_PASSWORD_REQUESTED,
    UPDATE_PASSWORD_INAPP_REQUESTED,
    UPDATE_PASSWORD_REQUESTED,
    ACCEPT_INVITE,
    defaultScopeId,
    failLoadCurrentUser,
    failLoginUrl,
    failLoginUser,
    failTriggerResetUserPassword,
    failUpdateUserPassword,
    failUpdateUserPasswordInApp,
    successLoadCurrentUser,
    successLoginUrl,
    successLoginUser,
    successLogoutUser,
    successTriggerResetUserPassword,
    successUpdateUserPassword,
    successUpdateUserPasswordInApp,
    userChanged,
} from "./auth.duck";
import {
    cleanupLocalstorageLoginItems,
    getAllAuthValuesInLocalStorage,
    getCurrentRedirectURI,
    getCurrentlyLoggingIn,
    getRefreshToken,
    getToken,
    resetAllAuthValuesInLocalStorage,
    setAllAuthValuesInLocalStorage,
    setCurrentRedirectURI,
    setCurrentlyLoggingIn,
} from "Utils/authLocalStorage";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

import { ONE_MINUTE, ONE_SECOND } from "Globals/constants/constants";
import { PAGEVISIBILITY } from "Globals/global.duck";

const v3Prefix = "mdv3";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failLoadCurrentUser,
        failLoginUrl,
        failLoginUser,
        failTriggerResetUserPassword,
        failUpdateUserPassword,
        failUpdateUserPasswordInApp,
        successLoadCurrentUser,
        successLoginUrl,
        successLoginUser,
        successLogoutUser,
        successTriggerResetUserPassword,
        successUpdateUserPassword,
        successUpdateUserPasswordInApp
    },
    getId,
    defaultScopeId
);

export const withApiContext = injectApiWithContextFactory(
    {
        acceptInvite,
        acceptTermsAndConditions,
        getInvites,
        getLoginUrl,
        loadCurrentUser,
        loginUser,
        logoutUser,
        redeemCode,
        refreshUserToken,
        triggerBackToApplication,
        triggerEmailVerification,
        triggerPasswordReset,
        updatePassword,
        updatePasswordInApp
    },
    injectBoundGQLClient
);


function* reloginModalSaga({
    api,
    api: { loginUser },
    actions: { successLoginUser = noop, failLoginUser = noop } = {}
}){
    yield put({ type: LOGIN_RELOGIN_REQUESTED });
    while (true){
        const { data: userData } = yield take(LOGIN_REQUESTED);
        try {
            const { 
                access_token: token,
                expires_in, // this is in seconds
                refresh_expires_in, // this is in seconds
                refresh_token: refreshToken,
                full: fullToken
            } = yield loginUser(
                userData
            );
    
            // eslint-disable-next-line no-magic-numbers
            const expiry = Date.now() + (expires_in * 1000);
            // eslint-disable-next-line no-magic-numbers
            const refreshExpiry = Date.now() + (refresh_expires_in * 1000);

            yield successLoginUser();
    
            return {
                expiry, token, refreshExpiry, refreshToken, fullToken 
            };
        } catch (e){
            console.error(e);
            yield failLoginUser(e);
            yield* startLoginSaga({ api });
        }
    }
}

let count = 0;

export function* refreshTokenSaga({
    refreshExpiry: currentRefreshExpiry,
    refreshToken: currentRefreshToken,
    api: { refreshUserToken = noop },
}) {
    try {
        if (count > 5){
            console.log("COUNT IS > 0 - refresh");
            return;
        }
        if (!currentRefreshToken) {
            count++;
            throw new Error("Unable to get refresh token!");
        }
        count = 0;
        if (Date.now() > currentRefreshExpiry) {
            throw new Error("Refresh token expired");
        }
        const {
            access_token: token,
            expires_in, // this is in seconds
            refresh_expires_in, // this is in seconds
            refresh_token: refreshToken,
            full: fullToken
        } = yield refreshUserToken({ refreshToken: currentRefreshToken });
        
        // eslint-disable-next-line no-magic-numbers
        const expiry = Date.now() + (expires_in * 1000);
        // eslint-disable-next-line no-magic-numbers
        const refreshExpiry = Date.now() + (refresh_expires_in * 1000);

        localStorage.setItem(`${v3Prefix}auth`, JSON.stringify({
            token,
            expiry,
            refreshExpiry,
            refreshToken,
            fullToken
        }));

        yield put(userChanged({ 
            loggedIn: true,
            token,
            expiry,
            refreshExpiry,
            refreshToken,
            fullToken
        }));

        return {
            expiry,
            refreshExpiry,
            refreshToken,
            token,
            fullToken
        };
    } catch (e) {
        console.error(e);
        throw e;
        // TODO: Need to check what error has occured here - we only want to retry on 401 or custom error above
        //const authvals = yield call(reloginModalSaga);
        //return authvals;
        // const userData = yield take(LOGIN_REQUESTED);
        // const { 
        //     access_token: token,
        //     expires_in, // this is in seconds
        //     refresh_expires_in, // this is in seconds
        //     refresh_token: refreshToken
        // } = yield call(
        //     loginUser,
        //     userData
        // );

        // // eslint-disable-next-line no-magic-numbers
        // const expiry = Date.now() + (expires_in * 1000);
        // // eslint-disable-next-line no-magic-numbers
        // const refreshExpiry = Date.now() + (refresh_expires_in * 1000);

        // yield call(setAllAuthValuesInLocalStorage, {
        //     expiry,
        //     hasAuth: true,
        //     refreshExpiry,
        //     refreshToken,
        //     token
        // });

        // return { expiry, token, refreshExpiry, refreshToken };
    }
}

const clearUserPremissions = function* () {
    yield call(resetAllAuthValuesInLocalStorage);
    yield call(cleanupLocalstorageLoginItems);
    window.location.reload();
    // later: dispatch action that updated top-level key in react tree to refresh + force-get new token
};

export function* logoutUserSaga({ api: { logoutUser = noop } }) {
    try {
        yield logoutUser();
    } catch (e) {
        console.error(e);
    } finally {
        localStorage.setItem(`${v3Prefix}auth`, JSON.stringify({}));
        yield put(userChanged({ 
            loggedIn: false,
        }));
    }
}

function* startLoginSaga({ api: { getLoginUrl = noop } }) {
    if (process.env.NODE_ENV === "development") {
        if (window.location.host.startsWith("localhost")) {
            return window.location = `http://127.0.0.1:${window.location.port}`;
        }
    }

    const noLogin = !window.app.config.v3UserManagementEnabled && !!window?.APPVERSION?.directory;
    if (noLogin) {
        return;
    }

    const currenturl = window.location.href;
    const isStorybook = !!process.env.STORYBOOK_IS_STORYBOOK;
    try {
        const loginUrl = yield getLoginUrl({ redirect_uri: currenturl });
        if (loginUrl && !isStorybook) {
            localStorage.setItem(`${v3Prefix}redirectUri`, currenturl);
            // eslint-disable-next-line require-atomic-updates
            window.location = loginUrl;
        }
    } catch (e) {
        console.log("ERR", e);
        throw e;
    }
}

function* loginSaga({ api: { loginUser = noop } }) {
    const {
        data: { formData, action },
    } = yield take(LOGIN_REQUESTED);
    yield loginUser({ formData, action });
}

function* acceptInvites({ api: { acceptInvite, getInvites } }) {
    try {
        const invites = yield getInvites();
        const projectTokens = invites.filter(({ type }) => type.startsWith("PROJECT")).map(({ token }) => token);
        const instanceTokens = invites.filter(({ type }) => type.startsWith("INSTANCE")).map(({ token }) => token);
        if (projectTokens.length || instanceTokens.length) {
            try {
                yield acceptInvite({
                    instanceTokens,
                    projectTokens
                });
                window.location.reload();
            } catch (error) {
                window.location.reload();   
            }            
        }
    } catch (e) {
        console.error(e);
    }
}

function* persistNewLogin({
    token,
    expiry,
    refreshExpiry,
    refreshToken,
    fullToken,
    api
}) {
    Object.entries({
        token,
        expiry,
        refreshExpiry,
        refreshToken,
        fullToken
    }).forEach(([ k, v ]) => {
        if (!v) {
            throw new Error(`Can't persist partial login! Missing ${k}`);
        }
    });
    localStorage.setItem(`${v3Prefix}auth`, JSON.stringify({
        token,
        expiry,
        refreshExpiry,
        refreshToken,
        fullToken
    }));
    yield all([
        put({
            type: LOGIN_SUCCEEDED,
            data: { token, expiry, refreshExpiry, refreshToken },
        }),
        put(userChanged({ 
            loggedIn: true,
            token,
            expiry,
            refreshExpiry,
            refreshToken,
            fullToken
        }))
    ]);
    yield* acceptInvites({ api });
    // If we are in v2, use the token to get a v2 session as well
    if (window?.app?.Duchess?.loginv3) {
        yield call(window?.app?.Duchess.loginv3, {
            username: "apikey",
            password: fullToken
        });

    }
}

const removeQueryParams = () => {
    const newUrl = `${window.location.origin}${window.location.hash}`;
    window.history.replaceState({}, document.title, newUrl);
};

function* redeemCodeSaga({ api: { redeemCode = noop } }){
    
    const redirect = localStorage.getItem(`${v3Prefix}redirectUri`);
    if (!redirect) {
        throw new Error("Missing redirect url");
    }
    try {
        const {
            access_token: newToken,
            expires_in, // this is in seconds
            refresh_expires_in, // this is in seconds
            refresh_token: newRefreshToken,
            full
        } = yield redeemCode({
            redirect_uri: redirect,
            params: window.location.search
        });

        // eslint-disable-next-line no-magic-numbers
        const newExpiry = Date.now() + (expires_in * 1000);
        // eslint-disable-next-line no-magic-numbers
        const newRefreshExpiry = Date.now() + (refresh_expires_in * 1000);

        removeQueryParams();
        return {
            expiry: newExpiry,
            hasAuth: true,
            refreshExpiry: newRefreshExpiry,
            refreshToken: newRefreshToken,
            token: newToken,
            fullToken: full
        };
    } catch(e) {
        removeQueryParams();
        console.error(e);
        throw e;
    }
}

export function* triggerUserResetPasswordSaga({
    data,
    api: { triggerPasswordReset = noop },
    actions: { failTriggerResetUserPassword, successTriggerResetUserPassword },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield triggerPasswordReset(data);
        yield successTriggerResetUserPassword(result);
        yield call(resolve);
    } catch (e) {
        yield failTriggerResetUserPassword(e);
        yield call(reject, e);
    }
}

export function* updateUserPasswordSaga({
    data,
    api: { updatePassword = noop },
    actions: { successUpdateUserPassword, failUpdateUserPassword },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield updatePassword(data);
        yield successUpdateUserPassword(result);
        yield call(resolve);
    } catch (e) {
        yield failUpdateUserPassword(e);
        yield call(reject, e);
    }
}

export function* updateUserPasswordInAppSaga({
    data,
    api: { updatePasswordInApp = noop },
    actions: { successUpdateUserPasswordInApp, failUpdateUserPasswordInApp },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield updatePasswordInApp(data);
        yield successUpdateUserPasswordInApp(result);
        yield call(resolve);
    } catch (e) {
        yield failUpdateUserPasswordInApp(e);
        yield call(reject, e);
    }
}

export function* loadCurrentUserSaga({
    api: { loadCurrentUser = noop },
    actions: { successLoadCurrentUser, failLoadCurrentUser },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield loadCurrentUser();
        yield successLoadCurrentUser(result);
        yield call(resolve);
    } catch (e) {
        yield failLoadCurrentUser(e);
        yield call(reject, e);
    }
}

export function* getLoginUrlSaga({
    data,
    api: { getLoginUrl = noop },
    actions: { successLoginUrl, failLoginUrl },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield getLoginUrl(data);
        yield successLoginUrl(result);
        yield call(resolve);
    } catch (e) {
        yield failLoginUrl(e);
        yield call(reject, e);
    }
}

export function* triggerBackToApplicationSaga({ data, api: { triggerBackToApplication = noop } }) {
    yield triggerBackToApplication(data);
}

export function* triggerEmailVerificationSaga({ data, api: { triggerEmailVerification = noop } }) {
    yield triggerEmailVerification(data);
}

export function* acceptTermsAndConditionsSaga({ data, api: { acceptTermsAndConditions = noop } }) {
    yield acceptTermsAndConditions(data);
}

export default () =>
    function* rootSaga() {
        yield takeEvery(
            TRIGGER_RESET_PASSWORD_REQUESTED,
            withApiContext(withBoundActions(triggerUserResetPasswordSaga))
        );

        yield takeEvery(
            UPDATE_PASSWORD_REQUESTED,
            withApiContext(withBoundActions(updateUserPasswordSaga))
        );

        yield takeEvery(
            UPDATE_PASSWORD_INAPP_REQUESTED,
            withApiContext(withBoundActions(updateUserPasswordInAppSaga))
        );

        yield takeEvery(
            LOAD_CURRENT_USER_REQUESTED,
            withRefreshRequest(withApiContext(withBoundActions(loadCurrentUserSaga)))
        );

        yield takeEvery(
            LOGIN_URL_REQUESTED,
            withApiContext(withBoundActions(getLoginUrlSaga))
        );

        yield takeEvery(
            TRIGGER_EMAIL_VERIFICATION_EMAIL,
            withApiContext(withBoundActions(triggerEmailVerificationSaga))
        );

        yield takeEvery(
            TRIGGER_BACK_TO_APPLICATION,
            withApiContext(withBoundActions(triggerBackToApplicationSaga))
        );

        yield takeEvery(
            REQUEST_ACCEPT_TERMS_AND_CONDITIONS,
            withApiContext(withBoundActions(acceptTermsAndConditionsSaga))
        );

        yield takeEvery(
            ACCEPT_INVITE,
            withApiContext(acceptInvites)
        )
    };
